import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import { getOverdueTasks } from '@/services/taskService';

const fmt = (ymd) => {
  if (!ymd) return '—';
  const p = String(ymd).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : ymd;
};

const severity = (days) => {
  if (days >= 30) return 'bg-red-600 text-white';
  if (days >= 14) return 'bg-red-100 text-red-700';
  if (days >= 7) return 'bg-orange-100 text-orange-700';
  return 'bg-amber-100 text-amber-800';
};

const OverdueModal = ({ onClose, onJump, onOpenTask }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    getOverdueTasks()
      .then((res) => { if (!cancelled) setTasks(res?.tasks ?? []); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load overdue tasks.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) =>
      String(t.title || '').toLowerCase().includes(q) ||
      String(t.chemist_name || '').toLowerCase().includes(q) ||
      (t.assignees || []).some((a) => String(a.name || '').toLowerCase().includes(q)));
  }, [tasks, query]);

  const oldest = tasks.length ? tasks[0].days_overdue : 0;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-red-50 flex-shrink-0">
          <WarningAmberIcon className="text-red-600" style={{ fontSize: 20 }} />
          <div className="min-w-0">
            <div className="font-bold text-[#002169]">All overdue tasks</div>
            {!loading && !error && (
              <div className="text-xs text-gray-500">
                {tasks.length} open · oldest {oldest} day{oldest === 1 ? '' : 's'} overdue
              </div>
            )}
          </div>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700 transition">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="px-5 py-2 border-b border-gray-100 flex-shrink-0">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by task, pharmacy or person…"
            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-gray-500">Loading…</div>}
          {error && <div className="p-6 text-center text-sm text-red-600">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500">
              {tasks.length === 0 ? 'Nothing overdue.' : 'No matches.'}
            </div>
          )}

          {filtered.map((t) => (
            <div key={t.id}
              className="flex items-center gap-3 px-5 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition">
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${severity(t.days_overdue)}`}>
                {t.days_overdue}d
              </span>

              <div className="min-w-0 flex-1">
                <button
                  onClick={() => onOpenTask?.(t.id)}
                  className="text-sm font-semibold text-[#002169] hover:underline text-left truncate block w-full">
                  {t.is_urgent && <span className="text-red-600 mr-1">!</span>}
                  {t.title}
                </button>
                <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                  <span className="flex items-center gap-1">
                    <CalendarTodayIcon style={{ fontSize: 11 }} /> due {fmt(t.due_date)}
                  </span>
                  {t.chemist_name && (
                    <span className="flex items-center gap-1 truncate">
                      <LocalPharmacyOutlinedIcon style={{ fontSize: 12 }} /> {t.chemist_name}
                    </span>
                  )}
                  <span className="truncate">
                    {(t.assignees || []).length
                      ? t.assignees.map((a) => a.name).filter(Boolean).join(', ')
                      : 'Unassigned'}
                  </span>
                </div>
              </div>

              <button
                onClick={() => onJump?.(t.due_date)}
                title={`Jump to ${t.due_date}`}
                className="text-[11px] font-semibold text-gray-500 border border-gray-200 rounded-lg px-2 py-1 hover:border-gray-400 hover:text-gray-800 transition flex-shrink-0">
                Go to day
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverdueModal;
